import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { HomeIcon, ActivityIcon, LockIcon, GearIcon, HistoryIcon } from './Icons';
import { AnimatedNumber } from './AnimatedNumber';
import { useAuthStore } from '../stores/useAuthStore';
import { useNotificationStore } from '../stores/useNotificationStore';
import { T } from '../lib/design-tokens';

const NAV_ITEMS = [
  { key: '/', label: 'Home', icon: HomeIcon },
  { key: '/operations', label: 'Operations', icon: ActivityIcon, badge: true },
  { key: '/vault', label: 'Vault', icon: LockIcon },
  { key: '/history', label: 'History', icon: HistoryIcon },
  { key: '/settings', label: 'Settings', icon: GearIcon },
] as const;

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: clerkUser } = useUser();
  const usage = useAuthStore((s) => s.usage);
  const user = useAuthStore((s) => s.user);
  const unreadCount = useNotificationStore((s) => s.unreadCount);

  const activePath = location.pathname;

  function isActive(key: string) {
    if (key === '/') return activePath === '/';
    return activePath.startsWith(key);
  }

  return (
    <div
      className="w-[220px] h-screen flex flex-col shrink-0"
      style={{
        borderRight: `1px solid ${T.glassBorder}`,
        background: T.surface,
        fontFamily: T.sans,
      }}
    >
      {/* Logo */}
      <div className="px-5 pt-7 pb-9 flex items-baseline">
        <span
          className="font-mono text-[17px] font-bold"
          style={{ letterSpacing: '-0.5px', color: T.cream }}
        >
          index
        </span>
        <span
          className="font-mono text-[17px] font-bold relative"
          style={{ color: T.gold }}
        >
          92
          <span
            className="absolute bottom-[-2px] left-0 right-0 h-[2px] rounded-sm"
            style={{ background: T.gold }}
          />
        </span>
      </div>

      {/* Nav */}
      <div className="px-2.5 flex flex-col gap-0.5">
        {NAV_ITEMS.map((item, i) => {
          const active = isActive(item.key);
          const Icon = item.icon;
          const badgeCount = 'badge' in item && item.badge ? unreadCount : 0;
          return (
            <button
              key={item.key}
              onClick={() => navigate(item.key)}
              className="flex items-center gap-[11px] w-full text-left border-none cursor-pointer"
              style={{
                padding: '10px 12px',
                borderRadius: 8,
                background: active ? T.cream08 : 'transparent',
                color: active ? T.cream : T.cream35,
                fontSize: 13,
                fontWeight: 500,
                fontFamily: T.sans,
                transition: 'all 0.2s',
                animation: `slideIn 0.4s ease ${i * 0.05}s both`,
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.background = T.cream04;
                  e.currentTarget.style.color = T.cream60;
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = T.cream35;
                }
              }}
            >
              <span style={{ opacity: active ? 0.9 : 0.4, transition: 'opacity 0.2s' }}>
                <Icon />
              </span>
              {item.label}
              {badgeCount > 0 && (
                <span
                  className="ml-auto flex items-center justify-center rounded-full font-bold"
                  style={{
                    width: 18,
                    height: 18,
                    background: T.gold,
                    color: T.bg,
                    fontSize: 10,
                    animation: 'pop 0.3s ease',
                  }}
                >
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Stats card — This Month */}
      <div
        className="mx-2.5 mt-auto rounded-[10px]"
        style={{
          padding: 16,
          background: T.goldGlow,
          border: `1px solid ${T.goldDim}`,
        }}
      >
        <div
          className="font-semibold uppercase mb-2.5"
          style={{
            fontSize: 10,
            color: T.cream35,
            letterSpacing: '0.1em',
            fontFamily: T.mono,
          }}
        >
          This Month
        </div>
        <div className="flex justify-between">
          <div>
            <div
              className="font-mono text-[22px] font-bold"
              style={{ color: T.cream }}
            >
              <AnimatedNumber value={usage?.voice.used ?? 0} />
            </div>
            <div style={{ fontSize: 10, color: T.cream35, fontFamily: T.mono }}>
              calls
            </div>
          </div>
          <div className="text-right">
            <div
              className="font-mono text-[22px] font-bold"
              style={{ color: T.green }}
            >
              <AnimatedNumber value={usage?.email.used ?? 0} />
            </div>
            <div style={{ fontSize: 10, color: T.cream35, fontFamily: T.mono }}>
              emails
            </div>
          </div>
        </div>
      </div>

      {/* User profile */}
      <div
        style={{
          padding: 10,
          borderTop: `1px solid ${T.glassBorder}`,
          marginTop: 12,
        }}
      >
        <div
          className="flex items-center gap-2.5 rounded-[10px] cursor-pointer transition-colors"
          style={{ padding: '10px 12px' }}
          onMouseEnter={(e) => (e.currentTarget.style.background = T.cream04)}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <div
            className="flex items-center justify-center rounded-full font-bold"
            style={{
              width: 32,
              height: 32,
              background: `linear-gradient(135deg, ${T.gold}, #8a6d30)`,
              color: T.bg,
              fontSize: 13,
            }}
          >
            {clerkUser?.firstName?.[0] ?? clerkUser?.username?.[0] ?? 'U'}
          </div>
          <div>
            <div
              className="font-semibold text-[13px]"
              style={{ color: T.cream, fontFamily: T.sans }}
            >
              {clerkUser?.firstName ?? clerkUser?.username ?? 'User'}
            </div>
            <div
              style={{
                fontSize: 10,
                color: T.cream35,
                fontFamily: T.mono,
              }}
            >
              {user?.subscriptionTier
                ? `${user.subscriptionTier.toUpperCase()}`
                : 'FREE'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
